import { useEffect, useState } from "preact/hooks";
import { Button } from "../ui/button";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import type { RetentionOffer } from "./retention-offers";

export interface PostShipmentRetentionBodyProps {
  offers: RetentionOffer[];
  onDelay: () => void;
  onChangeFrequency?: () => void;
  onExhausted: () => void;
}

export function PostShipmentRetentionBody({
  offers,
  onDelay,
  onChangeFrequency,
  onExhausted,
}: PostShipmentRetentionBodyProps) {
  // Track (offers, index) together so that when offers identity changes we
  // reset to 0 in a single setState call (avoids both ref-during-render and
  // setState-in-effect lint rules).
  const [[trackedOffers, index], setOfferState] = useState<
    [RetentionOffer[], number]
  >([offers, 0]);

  // New reason → new offers array identity → restart at the first offer.
  if (trackedOffers !== offers) {
    setOfferState([offers, 0]);
  }

  const currentIndex = trackedOffers === offers ? index : 0;
  const current = offers[currentIndex];

  // Nothing (left) to offer → let the parent advance to the cancel confirm.
  useEffect(() => {
    if (current == null) onExhausted();
  }, [current, onExhausted]);

  if (current == null) return null;

  const advance = () => {
    if (currentIndex + 1 >= offers.length) {
      onExhausted();
    } else {
      setOfferState([offers, currentIndex + 1]);
    }
  };

  if (current === "change_frequency") {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:px-2">
        <p
          className="tw:text-base tw:text-left tw:m-0"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          Would you like to change the frequency instead of cancelling?
        </p>
        <div className="tw:flex tw:flex-col tw:gap-4 tw:pt-1">
          <Button
            type="button"
            onClick={() => onChangeFrequency?.()}
            className={PILL_FILLED_CLASS}
          >
            Change frequency
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={advance}
            className={PILL_OUTLINE_CLASS}
          >
            No thanks
          </Button>
        </div>
      </div>
    );
  }

  // current === "delay"
  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:px-2">
      <p
        className="tw:text-base tw:text-left tw:m-0"
        style={{ color: "var(--so-text, #523D33)" }}
      >
        Would you like to delay instead of canceling?
      </p>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:pt-1">
        <Button type="button" onClick={onDelay} className={PILL_FILLED_CLASS}>
          Delay
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={advance}
          className={PILL_OUTLINE_CLASS}
        >
          No thanks
        </Button>
      </div>
    </div>
  );
}
